import { useCallback, useEffect, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { AppUsageStatsBar } from '../components/app-usage/AppUsageStats';
import { AppUsagePagesTab } from '../components/app-usage/AppUsagePagesTab';
import { AppUsageUsersTab } from '../components/app-usage/AppUsageUsersTab';
import { AppUsageAiTab } from '../components/app-usage/AppUsageAiTab';
import { fmtDateTime, fmtDate } from '../components/app-usage/format';
import {
    getAppUsageStats, getAppUsagePages, getAppUsageUsers, getAppUsageAi,
} from '../services/appUsageApi';
import type {
    AppUsageStats, AppUsagePageRow, AppUsageUserRow, AppUsageAiResponse, UsageWindow, UsageAppScope, UsageApp,
} from '../types/appUsage';

type TabKey = 'pages' | 'users' | 'ai';

const STATE_KEY = 'app-usage-state';
const WINDOWS: Array<{ value: UsageWindow; label: string }> = [
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 90 days' },
    { value: 'all', label: 'All collected' },
];

/**
 * The apps that record usage come from the API (stats.apps); until the first answer the select
 * knows only this app. 'all' is added here — and is where the page opens (Roman, 2026-10-07:
 * the statistic is about every app, the single apps are the drill-down).
 */
const DEFAULT_APP: UsageApp = { key: 'reporting', name: 'Reporting App', hasAi: true };
const ALL_APPS: UsageApp = { key: 'all', name: 'All apps' };

/** AI calls exist for the apps the API marks hasAi (all but Supplier Order Management). */
const hasAiTab = (app: UsageAppScope, apps: UsageApp[]) =>
    app === ALL_APPS.key || apps.some(a => a.key === app && a.hasAi === true);

function loadState(): { window: UsageWindow; tab: TabKey; app: UsageAppScope } {
    try {
        const raw = sessionStorage.getItem(STATE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw) as { window?: unknown; tab?: unknown; app?: unknown };
            const window = WINDOWS.some(w => w.value === parsed.window) ? parsed.window as UsageWindow : 30;
            const tab = parsed.tab === 'users' || parsed.tab === 'ai' ? parsed.tab : 'pages';
            // Checked against the API's list once stats arrive; the API itself falls back to 'reporting'.
            const app = typeof parsed.app === 'string' && /^[a-z0-9]{1,20}$/.test(parsed.app) ? parsed.app : ALL_APPS.key;
            return { window, tab, app };
        }
    } catch { /* fall through to defaults */ }
    return { window: 30, tab: 'pages', app: ALL_APPS.key };
}

export function AppUsagePage() {
    const { canView, isLoading: permsLoading } = usePagePermissions('/app-usage');
    const initial = loadState();
    const [window, setWindow] = useState<UsageWindow>(initial.window);
    const [tab, setTab] = useState<TabKey>(initial.tab);
    const [app, setApp] = useState<UsageAppScope>(initial.app);
    const [stats, setStats] = useState<AppUsageStats | null>(null);
    const [pages, setPages] = useState<AppUsagePageRow[]>([]);
    const [users, setUsers] = useState<AppUsageUserRow[]>([]);
    const [ai, setAi] = useState<AppUsageAiResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Flicking through the app select starts overlapping loads; only the newest may land.
    const loadSeq = useRef(0);

    useEffect(() => {
        try {
            sessionStorage.setItem(STATE_KEY, JSON.stringify({ window, tab, app }));
        } catch { /* storage unavailable — non-fatal */ }
    }, [window, tab, app]);

    const load = useCallback(async () => {
        const seq = ++loadSeq.current;
        setLoading(true);
        setError(null);
        // allSettled: a failing list must not blank the tiles (house pattern).
        const [statsRes, pagesRes, usersRes, aiRes] = await Promise.allSettled([
            getAppUsageStats(window, app),
            getAppUsagePages(window, app),
            getAppUsageUsers(window, app),
            getAppUsageAi(window, app),
        ]);
        if (seq !== loadSeq.current) return;
        if (statsRes.status === 'fulfilled') {
            setStats(statsRes.value);
            // An app the API does not know (stale session state) answers as 'reporting' — follow it.
            if (statsRes.value.app && statsRes.value.app !== app) setApp(statsRes.value.app);
        }
        if (pagesRes.status === 'fulfilled') setPages(pagesRes.value.pages);
        if (usersRes.status === 'fulfilled') setUsers(usersRes.value.users);
        if (aiRes.status === 'fulfilled') setAi(aiRes.value);
        const failed = [statsRes, pagesRes, usersRes, aiRes].filter(r => r.status === 'rejected');
        if (failed.length > 0) {
            const first = failed[0] as PromiseRejectedResult;
            setError(`${first.reason instanceof Error ? first.reason.message : 'Request failed'} (part of the page could not be loaded)`);
        }
        setLoading(false);
    }, [window, app]);

    useEffect(() => {
        if (canView) void load();
    }, [canView, load]);

    if (permsLoading) return null;
    if (!canView) {
        return (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                You do not have access to this page.
            </div>
        );
    }

    const knownApps = stats?.apps ?? [DEFAULT_APP];
    const showAi = hasAiTab(app, knownApps);
    const tabs: Array<{ key: TabKey; label: string }> = [
        { key: 'pages', label: 'Pages' },
        { key: 'users', label: 'Users' },
        ...(showAi ? [{ key: 'ai' as const, label: 'AI Costs' }] : []),
    ];
    const activeTab: TabKey = tab === 'ai' && !showAi ? 'pages' : tab;
    const appOptions = [...knownApps, ALL_APPS];
    if (!appOptions.some(o => o.key === app)) appOptions.unshift({ key: app, name: app });

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">App Usage</h1>
                    <p className="text-sm text-gray-500">
                        Who uses which page and feature of the Dolder apps, how often — and what the AI calls cost.
                    </p>
                    <p className="mt-1 flex items-center gap-1 text-xs text-gray-400">
                        <Info className="h-3 w-3" />
                        Collected for system improvement, licence hygiene and AI cost control — no content is recorded.
                    </p>
                </div>
                <div className="text-right text-xs text-gray-400">
                    {stats?.lastEventAt && <div>Data through {fmtDateTime(stats.lastEventAt)} (UTC)</div>}
                    {stats?.firstEventAt && <div>collecting since {fmtDate(stats.firstEventAt)}</div>}
                </div>
            </div>

            {stats && stats.tracker && !stats.tracker.trackingEnabled && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    Tracking is currently disabled ([Reporting].[AppUsageConfig].TrackingEnabled = 0) — no new events are being recorded.
                </div>
            )}
            {stats && stats.tracker && stats.tracker.dropped > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    {stats.tracker.dropped} event(s) were dropped on this API instance
                    {stats.tracker.lastFlushError ? ` — last error: ${stats.tracker.lastFlushError}` : ''}.
                </div>
            )}
            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}

            <AppUsageStatsBar stats={stats} />

            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
                <select
                    value={String(window)}
                    onChange={e => setWindow(e.target.value === 'all' ? 'all' : Number(e.target.value) as UsageWindow)}
                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                >
                    {WINDOWS.map(w => <option key={String(w.value)} value={String(w.value)}>{w.label}</option>)}
                </select>
                <select
                    value={app}
                    onChange={e => setApp(e.target.value)}
                    aria-label="App"
                    className="rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                >
                    {appOptions.map(o => <option key={o.key} value={o.key}>{o.name}</option>)}
                </select>
                <div className="flex rounded-md border border-gray-200 p-0.5">
                    {tabs.map(t => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setTab(t.key)}
                            className={`rounded px-3 py-1 text-sm font-medium transition ${
                                activeTab === t.key ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                {loading && <span className="text-xs text-gray-400">Loading…</span>}
            </div>

            {/* key={app}: an open drawer and its sort belong to one app's rows */}
            {activeTab === 'pages' && <AppUsagePagesTab key={app} pages={pages} window={window} app={app} />}
            {activeTab === 'users' && <AppUsageUsersTab key={app} users={users} window={window} app={app} showAi={showAi} />}
            {activeTab === 'ai' && <AppUsageAiTab ai={ai} />}
        </div>
    );
}
